import { MouseEvent, useCallback, useRef, useState } from 'react';
import { useLongPress } from '@shared/util/hooks';

/**
 * A menu a long press opens, for a touch device's stand-in for hover and right-click: the state
 * for a controlled Menu, and the props for the element pressed. The click that ends a long press is
 * swallowed, so it doesn't follow a link under the menu, and the browser's own long-press callout
 * is suppressed. Off when `isEnabled` is false (desktop), leaving the element's clicks alone.
 */
export function useLongPressMenu(isEnabled: boolean) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isLongPressRef = useRef(false);

  // The hook's pointer handlers, not View's onLongPress, which would make the element a button.
  const longPressProps = useLongPress(
    useCallback(() => {
      isLongPressRef.current = true;
      setIsMenuOpen(true);
    }, []),
  );
  const onMenuOpenChange = (isOpen: boolean) => {
    setIsMenuOpen(isOpen);
    if (!isOpen) {
      isLongPressRef.current = false;
    }
  };
  const onClickCapture = (event: MouseEvent) => {
    if (isLongPressRef.current) {
      isLongPressRef.current = false;
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const pressProps = isEnabled
    ? {
        ...longPressProps,
        onClickCapture,
        onContextMenu: (event: MouseEvent) => event.preventDefault(),
      }
    : {};
  return { isMenuOpen: isEnabled && isMenuOpen, onMenuOpenChange, pressProps };
}
